import { getColorFromString } from '@fluentui/react';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Helper function to pick properties from an object and rename the keys
 *
 * @param obj target object to pick properties from
 * @param keyMap object that maps new keys to keys of {@link obj}
 * @returns {object} new object where keys are keys from {@link keyMap} and values are corresponding property values from {@link obj}
 * @example
 * pickAndRename(
 *   {a: 10, b: '20', c: false}, // obj
 *   {e: 'a', f: 'b'} // keyMap
 * )
 * // returns {e: 10, f: '20'}
 * // (picked 'a' and 'b', renamed to 'e' and 'f')
 */
export const pickAndRename = <T, KeyMap extends Record<string, keyof T>>(
  obj: T,
  keyMap: KeyMap
): { [k in keyof KeyMap]: T[KeyMap[k]] } => {
  const keys: (keyof KeyMap)[] = Object.keys(keyMap);

  const result = {} as { [k in keyof KeyMap]: T[KeyMap[k]] };
  keys.forEach((k) => {
    result[k] = obj[keyMap[k]];
  });

  return result;
};

/**
 * Extract the UTC date (without time) from a {@link Date} object into an ISO string (i.e. yyyy-MM-dd)
 *
 * @param date
 * @returns {string | undefined} Formatted ISO date string (yyyy-MM-dd) if {@link date} is not nil, undefined otherwise
 */
export const formatUTCDateISO = (date: Date | undefined): string | undefined =>
  date ? formatInTimeZone(date, 'UTC', 'yyyy-MM-dd' as const) : undefined;

/**
 * Get the color hex with alpha value from a {@link color} and {@link alpha} value
 *
 * @param color
 * @param alpha
 */
export const getColorHexWithAlpha = (color: string, alpha: number): string =>
  `#${getColorFromString(color)?.hex}${Math.floor(255 * alpha)
    .toString(16)
    .padStart(2, '0')}`;
